import moment from "moment";
import { PopupModal } from "../../../components/Modal";
import Button from "../../../components/Button";
import Spinner from "../../../components/Spinners";
import { ClientBilling, CollectionMethod } from "../types";
import { useInvoiceEmailPreview } from "../hooks";

type PreviewData = { subject: string; html: string; isProjected: boolean };

/**
 * "Preview exactly the invoice email the customer receives" — renders the
 * standalone Autumn invoice (value-receipt / bill) email for the charge's month
 * in an iframe (never dangerouslySetInnerHTML). READ-ONLY: it never sends and
 * never writes a dedup row.
 *
 * The backend prefers the settled-charge receipt; before a charge it PROJECTS
 * the bill from the charge preview (isProjected → the email reads "Total to be
 * charged"). A 404 means there is nothing billable to preview for the period.
 */
export function InvoiceEmailPreviewModal({
  client,
  month,
  collectionMethod,
  daysUntilDue,
  onClose,
}: {
  client: ClientBilling;
  month: string; // "YYYY-MM"
  // The admin's modal billing choice, so the projected preview matches the send.
  collectionMethod?: CollectionMethod;
  daysUntilDue?: number;
  onClose: () => void;
}) {
  const [yearStr, monthStr] = month.split("-");
  const year = Number(yearStr);
  const monthNum = Number(monthStr);
  const monthLabel = moment(month, "YYYY-MM").format("MMMM YYYY");

  const preview = useInvoiceEmailPreview(client.userId, monthNum, year, true, {
    collectionMethod,
    daysUntilDue,
  });
  const data: PreviewData | null = preview.data ?? null;

  // A 404 means "nothing billable to preview" for this month (no settled charge,
  // nothing to project) — show a friendly empty state, not a hard error.
  const nothingToPreview =
    !data &&
    !preview.isLoading &&
    (preview.error as { response?: { status?: number } } | null)?.response
      ?.status === 404;
  const hardError =
    !data && !preview.isLoading && preview.isError && !nothingToPreview;

  const statusLabel = data
    ? data.isProjected
      ? "Projected — not yet charged"
      : "Sent on charge"
    : null;

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="!p-0 w-full max-w-[760px] max-h-[90vh] overflow-hidden mx-auto"
    >
      <div className="flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-border-primary shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs font-medium text-content-secondary uppercase">
              Invoice Email Preview · {monthLabel}
            </div>
            {statusLabel && (
              <span className="text-[0.6875rem] font-medium border border-border-secondary rounded-full py-0.5 px-2 text-content-secondary">
                {statusLabel}
              </span>
            )}
          </div>
          <div className="font-semibold text-content-primary mt-1">
            {data?.subject ?? (preview.isLoading ? "Loading…" : "—")}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden bg-surface-secondary">
          {preview.isLoading ? (
            <div className="flex flex-col items-center justify-center h-[70vh] gap-3">
              <Spinner className="text-brand-primary" />
              <span className="text-sm text-content-secondary">
                Loading the preview…
              </span>
            </div>
          ) : data ? (
            <iframe
              title="Invoice email preview"
              srcDoc={data.html}
              className="w-full h-[70vh] border-0 bg-white"
            />
          ) : nothingToPreview ? (
            <div className="flex flex-col items-center justify-center h-[70vh] gap-4 px-6 text-center">
              <div className="max-w-sm">
                <div className="font-semibold text-content-primary mb-1">
                  Nothing to preview yet
                </div>
                <p className="text-sm text-content-secondary">
                  There's nothing billable to invoice{" "}
                  {client.hotelName || "this client"} for {monthLabel} yet.
                </p>
              </div>
            </div>
          ) : hardError ? (
            <div className="flex items-center justify-center h-[70vh] px-6 text-center text-sm text-red-700">
              Couldn't load the preview. Please try again.
            </div>
          ) : null}
        </div>

        <div className="px-6 py-4 border-t border-border-primary flex items-center justify-end shrink-0">
          <Button variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </PopupModal>
  );
}
